import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Breadcrumbs,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import Layout from '../../components/layout/Layout';
import { axiosInstance } from '../../services/axiosInstance';
import { format } from 'date-fns';
import productService from '../../services/productService';

const ProductsReport = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [productHistory, setProductHistory] = useState(null);

  // Search products when query changes
  useEffect(() => {
    if (searchQuery.length >= 3) {
      searchProducts(searchQuery);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const searchProducts = async (query) => {
    try {
      if (!query || query.trim().length < 3) {
        setSearchResults([]);
        setError(query ? 'Please enter at least 3 characters to search' : null);
        return;
      }

      const results = await productService.searchProducts(query.trim());
      setSearchResults(results || []);
      setError(null);
    } catch (err) {
      console.error('Error searching products:', err);
      setSearchResults([]);
      setError('Failed to search products. Please try again.');
    }
  };

  const handleProductSelect = async (product) => {
    setSelectedProduct(product);
    setSearchQuery(product.name);
    setSearchResults([]);
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/product-history/${product.id}`);
      setProductHistory(response.data);
    } catch (err) {
      setError('Failed to fetch product history');
      setProductHistory(null);
    }
    setLoading(false);
  };

  const getTransactionTypeDisplay = (type) => {
    const types = {
      'PURCHASE': 'Purchase',
      'SALE': 'Sale',
      'RETURN': 'Return',
      'ADJUSTMENT': 'Adjustment',
      'ADJUSTMENT_ADD': 'Stock Added',
      'ADJUSTMENT_SUBTRACT': 'Stock Removed',
      'RETURN_TO_SUPPLIER': 'Return to Supplier'
    };
    return types[type] || type;
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      searchProducts(searchQuery);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} to="/" color="inherit">
            Dashboard
          </Link>
          <Link component={RouterLink} to="/reports" color="inherit">
            Reports
          </Link>
          <Typography color="textPrimary">Products Report</Typography>
        </Breadcrumbs>

        {/* Title */}
        <Typography variant="h4" sx={{ mb: 4 }}>
          Products Report
        </Typography>

        {/* Search Field with Button */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              label="Search Products by Name, Barcode, or SKU"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={{ mb: searchResults.length > 0 ? 2 : 0 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              sx={{ minWidth: '120px' }}
            >
              Search
            </Button>
          </Box>
          {searchResults.length > 0 && (
            <Paper sx={{ maxHeight: 200, overflow: 'auto' }}>
              {searchResults.map((product) => (
                <Box
                  key={product.id}
                  sx={{
                    p: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => handleProductSelect(product)}
                >
                  <Typography>{product.name}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    SKU: {product.sku} | Barcode: {product.barcode}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Paper>

        {selectedProduct && productHistory && (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Current Stock Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Current Stock
                    </Typography>
                    <Typography variant="h4">
                      {productHistory.currentQuantity}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Available Units
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Calculated Inventory Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Calculated Inventory
                    </Typography>
                    <Typography variant="h4">
                      {productHistory.calculatedQuantity}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      From All Transactions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Total Transactions Card */}
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Total Transactions
                    </Typography>
                    <Typography variant="h4">
                      {productHistory.transactionHistory ? productHistory.transactionHistory.length : 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      All Time
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Transaction History Table */}
            <Paper sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ p: 2 }}>
                Transaction History
              </Typography>
              {productHistory.transactionHistory && productHistory.transactionHistory.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Reference</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productHistory.transactionHistory.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell>{tx.id}</TableCell>
                          <TableCell>{getTransactionTypeDisplay(tx.type)}</TableCell>
                          <TableCell>{tx.quantity}</TableCell>
                          <TableCell>
                            {tx.transactionDate ? (() => {
                              try {
                                // Handle LocalDateTime array format [year, month, day, hour, minute, second]
                                // where month is 1-based from Java but needs to be 0-based for JavaScript Date
                                if (Array.isArray(tx.transactionDate) && tx.transactionDate.length >= 3) {
                                  const [year, month, day, hour = 0, minute = 0, second = 0] = tx.transactionDate;
                                  const date = new Date(year, month - 1, day, hour, minute, second);
                                  return format(date, 'yyyy-MM-dd HH:mm');
                                }
                                // Fallback for other date formats
                                else if (typeof tx.transactionDate === 'string') {
                                  return format(new Date(tx.transactionDate), 'yyyy-MM-dd HH:mm');
                                }
                                // Fallback for Date objects
                                else if (tx.transactionDate instanceof Date) {
                                  return format(tx.transactionDate, 'yyyy-MM-dd HH:mm');
                                }
                                return 'Invalid Date';
                              } catch (error) {
                                console.error('Error formatting transaction date:', error, tx.transactionDate);
                                return 'Invalid Date';
                              }
                            })() : '-'}
                          </TableCell>
                          <TableCell>{tx.reference}</TableCell>
                          <TableCell>{tx.notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                  No transaction history available.
                </Typography>
              )}
            </Paper>
          </>
        )}
      </Container>
    </Layout>
  );
};

export default ProductsReport;
